/**
 * The definition of all market asset properties used throughout the repo.
 *
 * A market asset represents each asset within a market/silo.
 */

import { Schema } from "@effect/schema";
import { Address } from "../address";
import { ProtocolKey } from "../protocol";
import { ChainKey } from "../chain";
import { Percent } from "../percent";
import { Usd } from "../usd";
import { Amount } from "../amount";
import {
  CollateralMerklReward,
  DebtMerklReward,
  ProtectedMerklReward,
} from "../merkl";
import { PointsItem } from "../points";
import { satisfies } from "effect/Function";

export const AssetKey = Schema.Literal(
  "base",
  "firstBridge",
  "secondBridge"
).annotations({ identifier: "AssetKey" });

export type AssetKey = typeof AssetKey.Type;
export const assetKeys = AssetKey.literals;
export const isAssetKey = Schema.is(AssetKey);

export const DepositMode = Schema.Literal(
  "defaultTrue",
  "defaultFalse",
  "protectedOnly"
).annotations({ identifier: "DepositMode" });

export type DepositMode = typeof DepositMode.Type;
export const depositModes = DepositMode.literals;
export const isDepositMode = Schema.is(DepositMode);
export const DEFAULT_DEPOSIT_MODE = satisfies<DepositMode>()("defaultTrue");

export const DepositType = Schema.Literal(
  "none",
  "collateral",
  "protected",
  "dual"
).annotations({ identifier: "DepositType" });

export type DepositType = typeof DepositType.Type;
export const depositTypes = DepositType.literals;
export const isDepositType = Schema.is(DepositType);

export type IrmConfig = typeof IrmConfig.Type;
export type IrmConfigEncoded = typeof IrmConfig.Encoded;

export const IrmConfig = Schema.Struct({
  uopt: Percent,
  ucrit: Percent,
  ulow: Percent,
  ki: Schema.BigInt,
  kcrit: Schema.BigInt,
  klow: Schema.BigInt,
  klin: Schema.BigInt,
  beta: Schema.BigInt,
  ri: Schema.BigInt,
  Tcrit: Schema.BigInt,
}).annotations({ identifier: "IrmConfig" });

export type MarketAsset = typeof MarketAsset.Type;
export type MarketAssetEncoded = typeof MarketAsset.Encoded;

export const MarketAsset = Schema.Struct({
  // market meta
  marketAddress: Address,
  protocolKey: ProtocolKey,
  chainKey: ChainKey,

  // token meta
  tokenAddress: Address,
  rawSymbol: Schema.String,
  symbol: Schema.String,
  rawName: Schema.String,
  name: Schema.String,
  decimals: Schema.Number,
  isNative: Schema.Boolean,

  // asset meta
  assetKey: AssetKey,
  priceProvider: Address,
  collateralShareToken: Address,
  protectedShareToken: Address,
  debtShareToken: Address,
  maxLtv: Percent,
  lt: Percent,
  irmAddress: Address,

  // token state
  priceUsd: Usd,
  priceNative: Amount,

  // asset state
  liquidity: Amount,
  liquidityUsd: Usd,
  utilization: Percent,
  tvlUsd: Usd,

  collateralTotal: Amount,
  collateralBaseApr: Percent,
  collateralRewardsApr: Percent,
  collateralMerklRewards: Schema.Array(CollateralMerklReward),
  collateralPoints: Schema.Array(PointsItem),
  collateralEmissionPerSecond: Amount,

  protectedTotal: Amount,
  protectedRewardsApr: Percent,
  protectedMerklRewards: Schema.Array(ProtectedMerklReward),
  protectedPoints: Schema.Array(PointsItem),
  protectedEmissionPerSecond: Amount,

  depositMode: DepositMode,
  depositTotalApr: Percent,

  debtTotal: Amount,
  debtBaseApr: Percent,
  debtRewardsApr: Percent,
  debtMerklRewards: Schema.Array(DebtMerklReward),
  isDebtAprFixed: Schema.Boolean,
  debtPoints: Schema.Array(PointsItem),
  debtTotalApr: Percent,
  debtEmissionPerSecond: Amount,

  isPaused: Schema.Boolean,
  isDisabledCollateral: Schema.Boolean,

  irmConfig: IrmConfig,

  // token user state
  tokenBalance: Amount,

  // asset user state
  collateralBalance: Amount,
  collateralBalanceUsd: Usd,
  protectedBalance: Amount,
  protectedBalanceUsd: Usd,
  depositBalanceUsd: Usd,
  debtBalance: Amount,
  projectedDebtBalance: Amount,
  debtBalanceUsd: Usd,

  // market user state
  depositLiquidationPriceUsd: Schema.NullOr(Usd),
  debtLiquidationPriceUsd: Schema.NullOr(Usd),
}).annotations({ identifier: "MarketAsset" });
